<template>
  <page-stepped
    :key="key"
    :title="title"
    :breadcrumbs="breadcrumbs"
    :steps="steps"
    :model="model"
    :recipients="recipients"
    :fetch-endpoint="fetchEndpoint"
    :save-endpoint="{}"
    :show-save-button="false"
    :rules="rules"
    :readonly="readonly"
    :hide-next-button="true"
    :validation="false"
    :hash="true"
    @calculate="calculateRecipients"
    @check="checkRecipientCountStatus"
  >
    <template #pre-actions>
      <calculate-recipients
        :model="model"
        :recipients="recipients"
        @calculate="calculateRecipients"
        @check="checkRecipientCountStatus"
        @list="listRecipients"
      />
      <el-dropdown
        v-if="!isTemplateCampaign"
        trigger="click"
        @command="handleActions"
      >
        <el-button type="primary" plain>
          <icon glyph="tick" />
          {{ $t('CampaignStepped_Btn_Save') }}
          <i class="el-icon-arrow-down el-icon--right" />
        </el-button>
        <el-dropdown-menu>
          <el-dropdown-item
            command="save"
            :disabled="readonly || loading"
            v-if="
              (!isTemplateCampaign && !isEditPage) ||
                (isEditPage && !isLive && !isPast)
            "
          >
            <icon class="icon" glyph="tick" />
            {{ $t('CampaignStepped_Btn_Save') }}
          </el-dropdown-item>
          <el-dropdown-item
            command="saveAsDraft"
            :disabled="readonly || loading"
            v-if="
              (!isTemplateCampaign && !isEditPage) ||
                (isEditPage && !isLive && !isPast)
            "
          >
            <icon class="icon" glyph="tick" />
            {{ $t('CampaignStepped_Btn_SaveAsDraft') }}
          </el-dropdown-item>
          <el-dropdown-item
            command="saveAsTemplate"
            :disabled="readonly || loading"
            v-if="
              !isCampaignSnapshotRoute &&
                !isTemplateCampaign &&
                isCampaignTemplatesEnabled
            "
          >
            <icon class="icon" glyph="file" />
            {{ $t('CampaignsPage_Button_SaveAsTemplate') }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
      <el-button
        v-if="
          model.id &&
            $can('updateProjectItem') &&
            (model.status === campaignStatus.RUNNING ||
              model.status === campaignStatus.CLOSED)
        "
        type="primary"
        plain
        :disabled="isPauseDisabled || isCampaignSnapshotRoute"
        @click="pauseWorkflow"
      >
        <i class="ico icon-pause"></i>
        {{ $t('CampaignStepped_Btn_Pause') }}
      </el-button>
      <el-button
        v-if="
          model.id &&
            $can('updateProjectItem') &&
            model.status === campaignStatus.PAUSED
        "
        type="primary"
        plain
        :disabled="isResumeDisabled || isCampaignSnapshotRoute"
        @click="resumeWorkflow"
      >
        <i class="ico icon-play"></i>
        {{ $t('CampaignStepped_Btn_Resume') }}
      </el-button>
      <el-button
        v-if="!isTemplateCampaign && (isLive || isPast)"
        :disabled="
          isViewCampaignRoute ||
            isCampaignSnapshotRoute ||
            !$can('sendPushCampaign') ||
            !isChannelSelected
        "
        type="primary"
        @click="validate(0)"
      >
        <i class="ico icon-check"></i>
        {{ updateButtonName }}
      </el-button>
      <el-button
        v-if="
          !readonly &&
            !isLive &&
            showReviewBtn &&
            !isPast &&
            !isTemplateCampaign
        "
        :disabled="readonly || !$can('sendPushCampaign') || !isChannelSelected"
        type="primary"
        @click="validate(0)"
      >
        <i class="ico icon-check"></i>
        {{ launchButtonName }}
      </el-button>
      <el-button
        v-if="readonly && showReviewBtn && isDraft"
        :disabled="
          !$can('sendPushCampaign') ||
            !isChannelSelected ||
            isCampaignSnapshotRoute
        "
        type="primary"
        @click="launch"
      >
        <i class="ico icon-check"></i>
        {{ $t('CampaignStepped_Btn_Launch') }}
      </el-button>
      <el-button
        class="btn-save-template"
        icon="el-icon-check"
        @click="validate(0)"
        v-if="isTemplateCampaign"
        :disabled="
          (!$can('sendPushCampaign') && !$isRole('editor')) || readonly
        "
      >
        {{ $t('CampaignStepped_Btn_SaveTemplate') }}
      </el-button>
      <review-dialog
        :errors="errors"
        :warnings="warnings"
        v-model="model"
        :initial-state-model.sync="initialStateModel"
        :dialog-visible.sync="dialogVisible"
        :recipients="recipients"
        :last-modified-data="lastModifiedData"
        :campaign-save-as-template="campaignSaveAsTemplate"
        :idempotency-key="idempotencyKey"
        @calculate="calculateRecipients"
        @check="checkRecipientCountStatus"
        @interrupt="interruptPing"
      />
    </template>
  </page-stepped>
</template>

<script>
import moment from 'moment'
import 'moment-timezone'
import PageStepped from '@/common/components/page/PageStepped'
import {
  customConfirm,
  customAlert,
  projectEndpoint,
  projectRoute,
  uuid
} from '@/core/helpers'
import StepAnalytics from '@/modules/campaign-manage/pages/StepAnalytics'
import StepSchedule from '@/modules/campaign-manage/pages/StepSchedule'
import StepSegment from '@/modules/campaign-manage/pages/StepSegment'
import StepReviewAndLaunch from '@/modules/campaign-manage/pages/StepReviewAndLaunch'
import StepEvents from '@/modules/campaign-manage/pages/StepEvents'
import StepAccumulation from '@/modules/campaign-manage/pages/StepAccumulation'
import StepContent from '@/modules/campaign-manage/pages/StepContent'
import StepSetup from '@/modules/campaign-manage/pages/StepSetup'
import StepPreview from '@/modules/campaign-manage/pages/StepPreview.vue'
import _ from 'lodash'
import {
  channelLabels,
  channelTypes
} from '@/modules/campaign-manage/enum/Labels'
import { defaultMessages } from '@/modules/campaign-manage/enum/DefaultMessages'
import StepLocations from '@/modules/campaign-manage/pages/StepLocations'
import { EventBus } from '@/core/EventBus'
import {
  $can,
  $isRole,
  $hasFeature,
  $hasPlatformChannel,
  $hasPlatformActiveAppForChannel
} from '@/core/utils/VueAcl'
import RestApi from '@/core/services/RestApi'
import ReviewDialog from '@/modules/campaign-manage/components/ReviewDialog'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { campaignEventModeEnum } from '@/modules/campaign-manage/enum/CampaignEventModeEnum'
import { campaignModelFields } from '@/modules/campaign-manage/enum/CampaignModelFields'
import { campaignSystemEvents } from '@/modules/campaigns/enum/CampaignSystemEventEnum'
import { campaignStatus } from '@/modules/campaign-manage/enum/CampaignStatusEnum'
import { mapGetters } from 'vuex'
import CalculateRecipients from '@/modules/campaign-manage/components/CalculateRecipients'
import { MessageTypeEnum } from '@/common/enum/MessageTypeEnum'
import { campaignSendTypeEnum } from '@/modules/campaigns/enum/CampaignSendTypeEnums'
import { sections } from '@/modules/campaign-manage/enum/CampaignModelDiffMap'
import CreateCampaignMixin from '@/modules/campaign-manage/mixins/CreateCampaignMixin'
import { isCampaignAccessible } from '@/modules/campaigns/helpers/CampaignPermissions'
import iframeLeaveConfirmationMixin from '@/common/mixins/iframeLeaveConfirmationMixin'
import iframeTitleMixin from '@/common/mixins/iframeTitleMixin'
import { isEmbedded } from '@/core/utils/EmbeddedPage'

export default {
  name: 'CreateCampaign',
  components: { CalculateRecipients, ReviewDialog, PageStepped },
  mixins: [CreateCampaignMixin, iframeLeaveConfirmationMixin, iframeTitleMixin],
  async created() {
    await this.$store.dispatch('fetchTimezones')
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  },
  beforeRouteLeave(to, from, next) {
    if (this.isAccessForbidden) {
      next()
      return
    }
    if (this.isCampaignSnapshotRoute) {
      next()
      return
    }
    if (this.confirmedViaPostMessage) {
      this.confirmedViaPostMessage = false
      next()
      return
    }
    if (
      this.$route.name === 'ViewTemplateCampaign' ||
      _.isEqual(this.model, this.initialStateModel) ||
      to.query.type
    ) {
      next()
    } else {
      this.discardChangesConfirm()
        .then(() => {
          next()
        })
        .catch(() => {
          next(false)
        })
    }
  },
  data() {
    return {
      idempotencyKey: uuid(),
      previewMode: false,
      key: null,
      workflowCopyIds: [],
      showReviewBtn: false,
      fields: campaignModelFields.join(','),
      campaignStatus,
      campaignSystemEvents,
      isPauseDisabled: false,
      isResumeDisabled: false,
      campaignSaveAsTemplate: false,
      errors: {},
      warnings: {},
      dialogVisible: false,
      initialLoad: true,
      initialStateModel: {},
      initialStateModelMessagesUpdated: false,
      lastModifiedData: {
        editStartTime: null,
        lastModified: null,
        lastModifier: null
      },
      channelTypes,
      defaultMessage: _.cloneDeep(defaultMessages),
      recipients: {
        taskId: 0,
        timer: 20,
        descriptiveCountTime: 0,
        polling: false,
        count: {
          channel: {},
          message_type: {}
        },
        buttonsDisabled: false,
        interruptPing: false,
        showRecipientsList: false,
        statusTimeoutObject: null
      },
      model: {
        postValidateDraftStatus: 1,
        snapshotPrevious: null,
        snapshotNext: null,
        isDeletedCampaign: null,
        updatedBy: null,
        id: null,
        title: '',
        draft: 0,
        active: null,
        messages: {},
        sms_addressable: 0,
        broadcast: 0,
        conversions: 0,
        event_conditions: null,
        category_id: null,
        goal_conditions: null,
        event_filter: false,
        facebook: 0,
        conditions: null,
        content_fetch: 0,
        content_fetch_sources: [],
        encryption: 1,
        languages_default: 'en',
        languages_list: ['en'],
        type: null,
        trigger: 0,
        promotion: 0,
        promotion_id: null,
        promotion_action: null,
        promotion_params: null,
        promotion_amount: null,
        promotion_amount_type: null,
        promotion_amount_attribute_title: null,
        promotion_amount_metric_title: null,
        promotion_amount_property_title: null,
        promotion_amount_percents: null,
        promotion_amount_round: null,
        promotion_amount_min: null,
        promotion_amount_cap: null,
        promotion_amount_window: null,
        promotion_amount_daterange: null,
        promotion_duration: null,
        ab: 0,
        ab_cg: 0,
        ab_cg_size: 0,
        ab_variants: ['A'],
        event: 'custom',
        event_title: null,
        event_mode:
          parseInt(this.$route.query.type) === campaignTypeEnum.TYPE_INAPP
            ? campaignEventModeEnum.SHOW
            : campaignEventModeEnum.SEND,
        event_delay_time: null,
        event_delay_period: null,
        event_limit_per_user: null,
        event_limit_per_user_scope: null,
        event_limit_per_user_period: null,
        event_limit_nth_hit: null,
        event_limit_nth_hit_scope: null,
        event_limit_nth_hit_period: null,
        location_limit_per_user: null,
        location_limit_per_user_period: null,
        location_limit_per_location: null,
        location_limit_per_location_period: null,
        location_limit_nth_hit: null,
        location_limit_nth_hit_period: null,
        languages_multi: 0,
        ios_push: 0,
        android_push: 0,
        android_push_production:
          $hasFeature('androidPushProduction') ||
          $hasFeature('androidPushProductionV1')
            ? 1
            : 0,
        android_push_legacy: $hasFeature('androidPushLegacy') ? 1 : 0,
        ios_push_production: $hasFeature('iosPushProduction') ? 1 : 0,
        ios_push_sandbox: 0,
        ios_push_legacy: $hasFeature('iosPushLegacy') ? 1 : 0,
        web_push: 0,
        email: 0,
        email_addressable: 0,
        sms: 0,
        inapp: 0,
        inapp_ios: 0,
        inapp_android: 0,
        inbox: 0,
        inbox_addressable: 1,
        onsite: 0,
        webhook: 0,
        whatsapp: 0,
        workflow: 0,
        webhook_addressable: 0,
        locations: [],
        location_trigger: null,
        location_dwell_time: null,
        target_applications: [],
        target_application_enabled: 0,
        variants: [
          {
            key: 'A',
            title: '',
            active: 1,
            promotion_id: null,
            promotion_action: null,
            promotion_params: null,
            promotion_amount: null,
            promotion_amount_type: null,
            promotion_amount_attribute_title: null,
            promotion_amount_metric_title: null,
            promotion_amount_property_title: null,
            promotion_amount_percents: null,
            promotion_amount_round: null,
            promotion_amount_min: null,
            promotion_amount_cap: null,
            promotion_amount_window: null,
            promotion_amount_daterange: null,
            promotion_duration: null,
            split_percentage: 100
          }
        ],
        web_push_safari: $hasFeature('hasSafari'),
        web_push_chrome: $hasFeature('hasChrome'),
        web_push_firefox: $hasFeature('hasFirefox'),
        send_type: campaignSendTypeEnum.SEND_TYPE_NOW,
        send_date: null,
        send_time: null,
        timezone: null,
        retry_type: 0,
        retry_for: 4,
        retry_for_period: 'weeks',
        retry_until: null,
        persist_type: this.$store.state.app.project.inbox_remove ? 2 : 0,
        persist_for: this.$store.state.app.project.inbox_remove
          ? this.$store.state.app.project.inbox_remove_after.count
          : null,
        persist_for_period:
          this.$store.state.app.project.inbox_remove &&
          this.$store.state.app.project.inbox_remove_after.period
            ? this.$store.state.app.project.inbox_remove_after.period
            : 'days',
        persist_until: new Date(),
        send_days: [1, 2, 3, 4, 5, 6, 7],
        send_frequency:
          (this.$route.query.type === campaignTypeEnum.TYPE_NOTIFICATION ||
            this.$route.query.type === campaignTypeEnum.TYPE_WORKFLOW) &&
          !this.$route.query.trigger
            ? 1
            : null,
        send_interval:
          (this.$route.query.type === campaignTypeEnum.TYPE_NOTIFICATION ||
            this.$route.query.type === campaignTypeEnum.TYPE_WORKFLOW) &&
          !this.$route.query.trigger
            ? 'weeks'
            : null,
        send_option: null,
        start_date: null,
        start_time: '00:00',
        start_date_time: null,
        end_date: null,
        end_time: '23:59',
        end_date_time: null,
        end_date_timezone: null,
        prepare_interval_enabled: 0,
        prepare_interval: null,
        optimove_type: 0,
        automatically_pick_winner: 0,
        automatically_pick_winner_after_n: 1,
        automatically_pick_winner_after_period: null,
        automatically_pick_winner_sample_channel: null,
        automatically_pick_winner_sample_type: null,
        automatically_pick_winner_sample_percentage: 100,
        automatically_pick_winner_schedule_status: null,
        seed_list_enabled: 0,
        ai_enabled: 0,
        is_campaign_template: 0,
        campaign_template_last_used: 0,
        create_time: 0,
        throttling: 0,
        throttling_data: null,
        rolling_segment_id: null,
        random_variant_allocation: 0,
        custom_variant_share: 0,
        early_cancel_event_title: null,
        early_cancel_event_filter: false,
        early_cancel_event_conditions: null,
        target_authorised_devices: 0,
        voucher_codes_enabled: 0,
        voucher_codes_file: null,
        target_devices: null,
        consent_preference: 0,
        consent_preference_name: null,
        enter_journey_once_ever: 0,
        enter_journey_once_at_a_time: 0,
        qualification: 0,
        qualification_event_title: null,
        qualification_event_filter: 0,
        qualification_event_conditions: null,
        qualification_time: null,
        qualification_period: null,
        accumulation_conditions: null,
        new_mxa:
          Number(this.$route.query.new_mxa) && this.$hasFeature('newMxa')
            ? 1
            : 0,
        mxa_migration: 0,
        prioritise_rcs: 0
      },
      oldMessages: {},
      localModel: {},
      loading: false,
      isAccessForbidden: false
    }
  },
  mounted() {
    EventBus.$on('campaignValidateDialog', draft => this.validate(draft))
    EventBus.$on('campaignLaunch', () => this.launch())
    EventBus.$on('updateInitialStateMessages', messages => {
      if (!this.initialStateModelMessagesUpdated) {
        this.$set(this.initialStateModel, 'messages', _.cloneDeep(messages))
        this.initialStateModelMessagesUpdated = true
      }
    })

    this.model.timezone = this.projectTimezone
    this.model.start_date = this.nowDate
    this.model.start_date_time = this.nowTime
    this.model.goal_conditions = _.cloneDeep(this.dataConditionDefault)
    this.model.event_conditions = _.cloneDeep(this.dataEventsConditionDefault)
    this.model.qualification_event_conditions = _.cloneDeep(
      this.dataQualificationEventConditionDefault
    )
    this.model.accumulation_conditions = _.cloneDeep(
      this.dataAccumulationConditionDefault
    )
    this.model.early_cancel_event_conditions = _.cloneDeep(
      this.dataEventsConditionDefault
    )
    this.model.conditions = _.cloneDeep(this.dataConditionDefault)
    this.updateFromRoute(this.$route)
    this.setBroadcastType()
    if (this.$store.getters.project.campaign_prepare_interval) {
      this.model.prepare_interval_enabled = 1
      this.model.prepare_interval = this.$store.state.app.project.campaign_prepare_interval
    }
    this.initialStateModel = _.cloneDeep(this.model)

    EventBus.$on('step-updated', step => {
      this.showReviewBtn = step.name === 'review'
    })
    EventBus.$on('force-reload-page', () => {
      this.key = new Date().getTime()
    })
    EventBus.$on('show-confirmation-before-create-dialog', toRoute => {
      if (_.isEqual(this.model, this.initialStateModel)) {
        if (this.isEmbeddedTemplateRoute) {
          EventBus.$emit('show-campaign-template-create-dialog', toRoute)
          return
        }

        EventBus.$emit('show-campaign-create-dialog', toRoute)
      } else {
        this.discardChangesConfirm().then(() => {
          if (this.isEmbeddedTemplateRoute) {
            EventBus.$emit('show-campaign-template-create-dialog', toRoute)
            return
          }

          EventBus.$emit('show-campaign-create-dialog', toRoute)
        })
      }
    })

    if (this.$route.query.id) {
      this.lastModifiedData.editStartTime = Math.floor(Date.now() / 1000)

      if (['UpdateCampaign', 'ViewCampaign'].includes(this.$route.name)) {
        this.saveRecentlyViewedCampaign(this.$route.query.id)
      }
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    this.checkAiEnablement()
  },
  destroyed() {
    EventBus.$off('show-confirmation-before-create-dialog')
    EventBus.$off('force-reload-page')
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    EventBus.$off('campaignValidateDialog')
    EventBus.$off('campaignLaunch')
    EventBus.$off('updateInitialState')
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
  },
  computed: {
    isViewCampaignRoute() {
      return (
        this.$route.name === 'ViewCampaign' ||
        this.$route.name === 'ViewTemplateCampaign'
      )
    },
    isCampaignSnapshotRoute() {
      return this.$route.name === 'ViewCampaignSnapshot'
    },
    isCampaignTemplatesEnabled() {
      return $hasFeature('campaignTemplates')
    },
    isTemplateCampaign() {
      return (
        this.isCampaignTemplatesEnabled &&
        (this.$route.name === 'CreateTemplateCampaign' ||
          this.$route.name === 'UpdateTemplateCampaign' ||
          this.$route.name === 'CopyTemplateCampaign' ||
          this.$route.name === 'ViewTemplateCampaign')
      )
    },
    isReadonly() {
      let winner = this.model.variants.find(variant => variant.winner)
      return (
        winner &&
        this.$route.query.variant !== 'W' &&
        this.$route.hash === '#content'
      )
    },
    isChannelSelected() {
      let selectedChannels = Object.keys(channelLabels).filter(
        c => this.model[c]
      )

      if (this.model.type === campaignTypeEnum.TYPE_WORKFLOW) {
        return true
      }

      if (this.model.promotion) {
        return true
      }

      return !!selectedChannels.length
    },
    deviceChannelIsActive() {
      return (
        this.model.ios_push === 1 ||
        this.model.android_push === 1 ||
        this.model.web_push === 1 ||
        this.model.inapp === 1 ||
        this.model.onsite === 1 ||
        (this.model.inbox === 1 && !$hasFeature('inboxProfileOn'))
      )
    },
    isEditPage() {
      return Number.isInteger(this.model.id)
    },
    isLive() {
      return this.model.active === 1
    },
    isDraft() {
      return this.model.draft === 1
    },
    isPast() {
      return (
        this.model.hasOwnProperty('status_canonical') &&
        this.model.status_canonical === 'past'
      )
    },
    steps() {
      let setup = {
        name: 'setup',
        title: this.$t('CampaignStep_Setup'),
        component: StepSetup,
        completed: false,
        model: this.model,
        isChanged: this.isTabChanged('setup')
      }
      let content = {
        name: 'content',
        title: this.$t('CampaignStep_Content'),
        component: StepContent,
        completed: false,
        model: this.model,
        isChanged: this.isTabChanged('content')
      }
      let events = {
        name: 'events',
        title: this.$t('CampaignStep_Events'),
        component: StepEvents,
        completed: false,
        model: this.model,
        isChanged: this.isTabChanged('events')
      }
      let accumulation = {
        name: 'accumulation',
        title: this.$t('CampaignStep_Accumulation'),
        component: StepAccumulation,
        completed: false,
        model: this.model,
        isChanged: this.isTabChanged('accumulation')
      }
      let locations = {
        name: 'locations',
        title: this.$t('CampaignStep_Locations'),
        component: StepLocations,
        completed: false,
        model: this.model,
        isChanged: this.isTabChanged('locations')
      }
      let segments = {
        name: 'segment',
        title: this.$t('CampaignStep_Segment'),
        component: StepSegment,
        completed: false,
        model: this.model,
        isChanged: this.isTabChanged('segment')
      }
      let preview = {
        name: 'preview',
        title: this.$t('CampaignStep_Preview'),
        component: StepPreview,
        completed: false,
        model: this.model,
        isChanged: this.isTabChanged('preview'),
        customTabStyles: {
          padding: 0
        }
      }
      let schedule = {
        name: 'schedule',
        title: this.$t('CampaignStep_Schedule'),
        component: StepSchedule,
        completed: false,
        model: this.model,
        isChanged: this.isTabChanged('schedule')
      }
      let review = {
        name: 'review',
        title: this.reviewTabTitle,
        component: StepReviewAndLaunch,
        completed: false,
        model: this.model,
        recipients: this.recipients,
        isChanged: this.isTabChanged('review')
      }
      let analytics = {
        name: 'analytics',
        title: this.$t('CampaignStep_Analytics'),
        component: StepAnalytics,
        completed: false,
        model: this.model,
        isChanged: false
      }
      let steps = []
      steps.push(setup)
      steps.push(content)
      if (
        this.model.trigger === campaignTriggerTypes.TRIGGER_EVENT ||
        this.model.type === campaignTypeEnum.TYPE_INAPP
      ) {
        steps.push(events)
      }
      if (this.model.trigger === campaignTriggerTypes.TRIGGER_ACCUMULATION) {
        steps.push(accumulation)
      }
      if (this.model.trigger === campaignTriggerTypes.TRIGGER_LOCATION) {
        steps.push(locations)
      }
      steps.push(segments)
      if (this.showPreviewTab) {
        steps.push(preview)
      }
      steps.push(schedule)
      steps.push(review)
      if (
        this.model.id &&
        !this.model.draft &&
        !this.model.is_campaign_template
      ) {
        steps.push(analytics)
      }

      return steps
    },
    showPreviewTab() {
      const extendedPreviewEnabled = $hasFeature('extendedContentPreview')
      const hasPreviewableChannel =
        this.model.email ||
        (extendedPreviewEnabled &&
          (this.model.ios_push ||
            this.model.android_push ||
            this.model.inapp ||
            this.model.inbox))

      if (
        this.model.type !== campaignTypeEnum.TYPE_WORKFLOW &&
        !hasPreviewableChannel
      ) {
        return false
      }

      if ($isRole('admin') || $isRole('admin-support')) {
        return true
      }

      if (!$hasFeature('campaignContentPreview')) {
        return false
      }

      return !(
        !$can('sendPushCampaign') &&
        !$can('createProjectItem') &&
        !$can('updateProjectItem')
      )
    },
    embeddedPageTitle() {
      return this.isEditPage ? this.model.title : null
    },
    title() {
      if (this.$store.getters.embeddedTitle) {
        return this.$store.getters.embeddedTitle
      }
      if (!this.isEditPage) {
        return this.isTemplateCampaign
          ? this.$t('Campaigns_Breadcrumb_CreateCampaignTemplate')
          : this.$t('Campaigns_Breadcrumb_CreateCampaign')
      }
      return this.model.title
    },
    breadcrumbs() {
      return [
        {
          label: this.$t('Campaigns_Breadcrumb_Campaigns')
        },
        {
          label: this.title
        }
      ]
    },
    messages() {
      return this.model.messages
    },
    readonly() {
      return (
        this.isViewCampaignRoute ||
        this.isCampaignSnapshotRoute ||
        this.isReadonly ||
        this.previewMode
      )
    },
    fetchEndpoint() {
      return [this.fetchProjectEndpoint, this.fetchCampaign]
    },
    fetchProjectEndpoint() {
      if (this.$route.query.project_id) {
        return projectEndpoint({
          method: 'get',
          url: ``,
          params: {
            expand: 'content_fetch,rcs_available'
          },
          handler: data => {
            this.model.project = data
            this.initialStateModel.project = _.cloneDeep(data)
          }
        })
      } else {
        return null
      }
    },

    fetchCampaign() {
      if (this.$route.query.template) {
        return {
          method: 'get',
          url: this.$route.query.is_shared
            ? 'shared-campaign-templates/create-from-template'
            : 'customer-intelligence/lifecycle-journey-templates/template',
          params: {
            id: this.$route.query.template,
            project_id: this.$route.query.project_id,
            expand: this.fields,
            from: 'template',
            for: 'vue'
          },
          handler: data => {
            this.validateJourneyBuilderAccess(data)
            this.processData(data)
          }
        }
      }

      if (!this.$route.query.id) {
        return null
      }

      if (this.isCampaignSnapshotRoute) {
        return projectEndpoint({
          method: 'get',
          url: 'auditlog/campaign-snapshot',
          params: {
            id: this.$route.query.id,
            create_time: this.$route.query.create_time,
            expand: 'value,previous,next,is_deleted'
          },
          handler: data => {
            if (data.length === 0) {
              return
            }

            const record = data[0].value

            record.updated_by = data[0].updated_by
            record.create_time = data[0].create_time
            record.snapshotPrevious = data[0].previous
            record.snapshotNext = data[0].next
            record.isDeletedCampaign = data[0].is_deleted
            record.changedTabs = data[0].changed_tabs

            this.processData(record)
            this.$nextTick(() => {
              this.model.diff = []
              if (this.model.snapshotPrevious) {
                const modelPrevious = this.processData(
                  this.model.snapshotPrevious.value,
                  false
                )
                let diff = this.getCampaignDiff(this.model, {
                  ...this.model,
                  ...modelPrevious
                })
                this.model.diff = _.reduce(
                  sections,
                  (result, value, key) => {
                    return value.some(r => diff.indexOf(r) >= 0)
                      ? result.concat(key)
                      : result
                  },
                  []
                )
              }
            })
          }
        })
      }

      return projectEndpoint({
        method: 'get',
        url: `campaigns/${this.$route.query.id}`,
        params: {
          expand: this.fields,
          for: 'vue'
        },
        handler: data => {
          this.validateJourneyBuilderAccess(data)
          this.processData(data)
        }
      })
    },
    dataGoalConditionDefault() {
      return {
        operator: 'OR',
        '0': {
          operator: 'OR',
          '0': {}
        }
      }
    },
    dataConditionDefault() {
      return {
        operator: 'AND',
        '0': {
          operator: 'AND',
          '0': {}
        }
      }
    },
    dataEventsConditionDefault() {
      return {
        0: { 0: { 0: 'Select Type' }, operator: 'AND' },
        operator: 'AND'
      }
    },
    dataAccumulationConditionDefault() {
      return {
        operator: 'AND',
        '0': {
          operator: 'AND',
          '0': {}
        }
      }
    },
    dataQualificationEventConditionDefault() {
      return {
        0: { 0: { 0: 'Select Type' }, operator: 'AND' },
        operator: 'AND'
      }
    },
    rules() {
      return {}
    },
    nowDate() {
      return moment()
        .tz(this.projectTimezone)
        .format('Y-MM-DD')
    },
    nowTime() {
      return moment()
        .tz(this.projectTimezone)
        .format('HH:mm')
    },
    launchButtonName() {
      if (this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE) {
        return this.$t('CampaignStepped_Btn_SaveOptimoveTemplate')
      }

      return this.$t('CampaignStepped_Btn_ReviewAndLaunch')
    },
    updateButtonName() {
      if (this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE) {
        return this.$t('CampaignStepped_Btn_SaveOptimoveTemplate')
      }

      return this.$t('CampaignStepped_Btn_ReviewAndUpdate')
    },
    saveEndpoint() {
      if (this.model.id) {
        return projectEndpoint({
          method: 'patch',
          url: `campaigns/${this.model.id}`,
          data: this.localModel,
          headers: {
            'Idempotency-Key': this.model.id
          }
        })
      } else {
        return projectEndpoint({
          method: 'post',
          url: 'campaigns',
          data: this.localModel,
          headers: {
            'Idempotency-Key': this.idempotencyKey
          }
        })
      }
    },
    reviewTabTitle() {
      return this.isTemplateCampaign
        ? this.$t('CampaignStep_Review_Template')
        : this.$t('CampaignStep_Review')
    },
    isMaxautomationEnabled() {
      return $hasFeature('maxautomation')
    },
    isLegacyMxaEnabled() {
      return $hasFeature('legacyMxaEnabled')
    },
    isJbTwoEnabled() {
      return $hasFeature('newMxa')
    },
    isCopyCampaignRoute() {
      return this.$route.name === 'CopyCampaign'
    },
    isCopyTemplateCampaignRoute() {
      return this.$route.name === 'CopyTemplateCampaign'
    },
    isCreateFromTemplateCampaignRoute() {
      return this.$route.name === 'CreateFromTemplate'
    },
    isValidateJourneyBuilderAccessRoute() {
      return (
        this.isCopyCampaignRoute ||
        this.isCopyTemplateCampaignRoute ||
        this.isCreateFromTemplateCampaignRoute
      )
    },
    isEmbeddedTemplateRoute() {
      return (
        isEmbedded() && ['CreateTemplateCampaign'].includes(this.$route.name)
      )
    },
    ...mapGetters(['projectTimezone'])
  },
  methods: {
    validateJourneyBuilderAccess(campaign) {
      if (!this.isValidateJourneyBuilderAccessRoute) {
        return
      }
      if (
        !isCampaignAccessible(campaign, {
          maxautomation: this.isMaxautomationEnabled,
          legacyMxaEnabled: this.isLegacyMxaEnabled,
          newMxa: this.isJbTwoEnabled
        })
      ) {
        this.isAccessForbidden = true
        this.$router.replace(
          projectRoute({
            name: 'Forbidden'
          })
        )
      }
    },
    isTabChanged(tab) {
      if (!this.isCampaignSnapshotRoute) {
        return false
      }
      return this.model.changedTabs && this.model.changedTabs.includes(tab)
    },
    beforeUnloadHandler: function handler(event) {
      if (
        !_.isEqual(this.model, this.initialStateModel) &&
        !this.isCampaignSnapshotRoute
      ) {
        event.preventDefault()
        event.returnValue = ''
      } else {
        delete event['returnValue']
      }
    },
    launch() {
      this.$msgbox({
        title: this.$t('CampaignStepped_Btn_Launch_Confirm_Title'),
        message: this.$t('CampaignStepped_Btn_Launch_Confirm_Text'),
        showCancelButton: true,
        confirmButtonText: this.$t('CampaignStepped_Btn_Launch'),
        cancelButtonText: this.$t('RestList_SaveCancelButton'),
        center: true
      }).then(() => {
        RestApi(
          projectEndpoint({
            method: 'post',
            url: `campaigns/${this.model.id}/launch`,
            params: { id: this.model.id }
          })
        )
          .then(() => {
            if (this.model.send_type === campaignSendTypeEnum.SEND_TYPE_NOW) {
              this.$router.push(
                projectRoute({
                  name: 'CampaignExecutions',
                  query: { id: this.model.id }
                })
              )
            } else {
              this.$router.push(projectRoute({ name: 'AllCampaignsTab' }))
            }
          })
          .catch(data => {
            if (data.message) {
              this.$message({
                message: data.message,
                type: 'error'
              })
            }
          })
      })
    },
    async validate(draft, campaignSaveAsTemplate = 0) {
      if (this.recipients.polling) {
        await this.cancelCalculation()
      }

      if (this.$refs['popover'] && this.$refs['popover'].showPopper) {
        this.$refs['popover'].showPopper = false
      }
      if (this.isTemplateCampaign && !draft) {
        this.model.is_campaign_template = 1
      }
      if (!this.deviceChannelIsActive) {
        this.model.target_authorised_devices = 0
      }
      this.model.postValidateDraftStatus = draft

      const data = _.cloneDeep(this.model)
      data.draft = draft
      if (data.persist_type === 1 && data.persist_until) {
        data.persist_until = Math.round(data.persist_until / 1000)
      } else {
        data.persist_until = null
      }

      if (campaignSaveAsTemplate) {
        data.is_campaign_template = 1
      }

      if (!data.event_limit_per_user) {
        data.event_limit_per_user = ''
      }

      if (data.retry_until) {
        data.retry_until = Math.round(data.retry_until / 1000)
      }

      if (data.target_applications) {
        data.target_applications = data.target_applications.join(',')
      }
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaigns/validate`,
          config: { headers: { 'Content-Type': 'multipart/form-data' } },
          data: data
        })
      )
        .then(({ data }) => {
          this.errors = {}
          this.warnings = data.warnings || {}
          this.dialogVisible = true
          this.campaignSaveAsTemplate = !!campaignSaveAsTemplate
        })
        .catch(data => {
          if (data.message) {
            this.$message({
              message: data.message,
              type: 'error'
            })
          } else {
            this.errors = data.response.data.errors
            this.warnings = data.response.data.warnings || {}
            this.dialogVisible = true
            this.campaignSaveAsTemplate = !!campaignSaveAsTemplate
          }
        })
    },
    async cancelCalculation() {
      const channels = Object.keys(this.model.messages).map(key =>
        parseInt(key)
      )

      if (
        this.$store.state.app.project.calculate_recipients ||
        channels.includes(this.channelTypes.sms) ||
        channels.includes(this.channelTypes.whatsapp)
      ) {
        this.interruptPing()
        // add a delay in case we've not managed to stop the task in time
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    },
    copyWorkflow(campaignId) {
      return RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaigns/copy-workflow`,
          params: {
            campaign_id: campaignId
          },
          data: {
            features: {
              voucher_codes: !!this.model.voucher_codes_enabled,
              new_mxa: !!this.model.new_mxa
            }
          }
        })
      )
        .then(({ data }) => {
          data.forEach(workflow => {
            this.workflowCopyIds.push(workflow.id)
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })

          if (this.initialLoad) {
            const variants = this.model.messages[MessageTypeEnum.WORKFLOW]
            for (const variant in variants) {
              variants[variant].content.workflow_id = null
            }

            this.initialLoad = false
          }
        })
    },
    updateMessage(channel, selected = true) {
      if (!selected) {
        this.$delete(this.model.messages, channel)
        return
      }
      if (!this.model.messages[channel]) {
        this.$set(this.model.messages, channel, {})
      }
      this.model.variants.forEach((v, index) => {
        if (index && !this.model.ab) {
          this.$delete(this.model.messages[channel], v.key)
        } else {
          this.addNewVariant(channel, v.key)
        }
      })
    },

    // This function helps do not lose data when user enables multi-variant or multi-language
    getDefaultMsg(ch, variant, lang = null) {
      let defaultLang = this.model.languages_default
      let defaultMsg = this.defaultMessage[ch]

      if (!this.messages[ch].hasOwnProperty(variant)) {
        // Try to restore a message
        if (lang) {
          if (_.has(this.oldMessages, [ch, variant, 'languages', lang])) {
            return _.cloneDeep(this.oldMessages[ch][variant].languages[lang])
          }
        } else {
          if (_.has(this.oldMessages, [ch, variant, 'content'])) {
            return _.cloneDeep(this.oldMessages[ch][variant].content)
          }
        }
        return _.cloneDeep(defaultMsg)
      }

      // Non multi-lang campaign
      if (this.messages[ch][variant].hasOwnProperty('content')) {
        // Do not apply defaultLang message for all languages
        if (lang && lang !== defaultLang) {
          // Try to restore a message
          if (_.has(this.oldMessages, [ch, variant, 'languages', lang])) {
            return _.cloneDeep(this.oldMessages[ch][variant].languages[lang])
          }
          return _.cloneDeep(defaultMsg)
        }
        defaultMsg = this.messages[ch][variant].content
      }
      // Multi lang campaign
      if (this.messages[ch][variant].hasOwnProperty('languages')) {
        if (lang && this.messages[ch][variant].languages.hasOwnProperty(lang)) {
          defaultMsg = this.messages[ch][variant].languages[lang]
        } else if (
          !lang &&
          defaultLang &&
          this.messages[ch][variant].languages.hasOwnProperty(defaultLang)
        ) {
          defaultMsg = this.messages[ch][variant].languages[defaultLang]
        }
      }

      return _.cloneDeep(defaultMsg)
    },

    addNewVariant(ch, variant) {
      if (this.model.languages_multi) {
        let languages = {}
        this.model.languages_list.forEach(lang => {
          languages[lang] = this.getDefaultMsg(ch, variant, lang)
        })
        this.$set(this.model.messages[ch], variant, { languages })
      } else {
        this.$set(this.model.messages[ch], variant, {
          content: this.getDefaultMsg(ch, variant)
        })
      }
    },
    updateVariants() {
      if (!Object.keys(this.model.messages).length) {
        return
      }
      Object.keys(this.messages).forEach(ch => {
        this.model.variants.forEach(variant => {
          if (!this.messages[ch].hasOwnProperty(variant.key)) {
            this.addNewVariant(ch, variant.key)
          }
        })
        Object.keys(this.model.messages[ch]).forEach(variant => {
          if (!this.model.variants.find(v => v.key === variant)) {
            this.$delete(this.model.messages[ch], variant)
          }
        })
      })
    },
    updateLanguages() {
      if (!this.messages && !Object.keys(this.messages).length) {
        return
      }
      Object.keys(this.messages).forEach(ch => {
        this.model.variants.forEach((v, index) => {
          if (
            (index && !this.model.ab) ||
            !this.messages[ch][v.key].hasOwnProperty('languages')
          ) {
            return
          }
          this.model.languages_list.forEach(lang => {
            if (!this.messages[ch][v.key]['languages'].hasOwnProperty(lang)) {
              this.$set(
                this.messages[ch][v.key]['languages'],
                lang,
                this.getDefaultMsg(ch, v.key, lang)
              )
            }
          })

          Object.keys(this.messages[ch][v.key]['languages']).forEach(lang => {
            if (!this.model.languages_list.includes(lang)) {
              this.$delete(this.messages[ch][v.key]['languages'], lang)
            }
          })
        })
      })
    },
    updatePromotionVariants() {
      Object.keys(this.model.variants).forEach(v => {
        if (!this.model.variants[v].promotion_id) {
          Object.assign(this.model.variants[v], {
            promotion_id: this.model.promotion_id,
            promotion_action: this.model.promotion_action,
            promotion_params: this.model.promotion_params,
            promotion_amount: this.model.promotion_amount,
            promotion_amount_type: this.model.promotion_amount_type,
            promotion_amount_attribute_title: this.model
              .promotion_amount_attribute_title,
            promotion_amount_metric_title: this.model
              .promotion_amount_metric_title,
            promotion_amount_property_title: this.model
              .promotion_amount_property_title,
            promotion_amount_percents: this.model.promotion_amount_percents,
            promotion_amount_round: this.model.promotion_amount_round,
            promotion_amount_min: this.model.promotion_amount_min,
            promotion_amount_cap: this.model.promotion_amount_cap,
            promotion_amount_window: this.model.promotion_amount_window,
            promotion_amount_daterange: this.model.promotion_amount_daterange,
            promotion_duration: this.model.promotion_duration
          })
        }
      })
    },
    updateFromRoute(to) {
      if (to.query.type) {
        this.$set(this.model, 'type', parseInt(to.query.type))
      }
      if (to.query.trigger) {
        if (this.model.trigger === 0) {
          this.$set(this.model, 'trigger', parseInt(to.query.trigger))
        }
        let sendTypeValue
        to.query.template === 'WinBackYourCustomers'
          ? (sendTypeValue = campaignSendTypeEnum.SEND_TYPE_NOW)
          : (sendTypeValue = campaignSendTypeEnum.SEND_TYPE_SCHEDULE)
        this.$set(this.model, 'send_type', sendTypeValue)
      }
    },
    setBroadcastType() {
      if (!this.$route.query.id) {
        this.$set(
          this.model,
          'broadcast',
          $hasFeature('broadcastDisabled') ||
            (!$hasFeature('allowBroadcastForMultistageTimebasedCampaigns') &&
              this.model.type === campaignTypeEnum.TYPE_WORKFLOW &&
              this.model.trigger === campaignTriggerTypes.TRIGGER_NONE)
            ? 0
            : 1
        )
      }
    },
    pauseWorkflow() {
      customConfirm(
        this.$t('Campaign_Dialog_WorkflowPause_Title'),
        this.$t('Campaign_Dialog_WorkflowPause_Message'),
        this.$t('Campaign_Dialog_Button_Pause'),
        this.$t('Campaign_Dialog_Button_Cancel')
      ).then(() => {
        this.isPauseDisabled = true
        RestApi(
          projectEndpoint({
            method: 'post',
            url: `campaigns/${this.model.id}/pause`
          })
        )
          .then(({ data }) => {
            this.model.status = data.statusCode
            this.isPauseDisabled = false
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.isPauseDisabled = false
          })
      })
    },
    resumeWorkflow() {
      customConfirm(
        this.$t('Campaign_Dialog_WorkflowResume_Title'),
        this.$t('Campaign_Dialog_WorkflowResume_Message'),
        this.$t('Campaign_Dialog_Button_Resume'),
        this.$t('Campaign_Dialog_Button_Cancel')
      ).then(() => {
        this.isResumeDisabled = true
        RestApi(
          projectEndpoint({
            method: 'post',
            url: `campaigns/${this.model.id}/resume`
          })
        )
          .then(({ data }) => {
            this.model.status = data.statusCode
            this.isResumeDisabled = false
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.isResumeDisabled = false
          })
      })
    },
    processData(data, setModelData = true) {
      if (
        this.$route.name === 'CopyCampaign' ||
        this.$route.name === 'CreateFromTemplate' ||
        this.$route.name === 'CopyTemplateCampaign' ||
        this.$route.query.template
      ) {
        data.id = null
        data.active = 0
        delete data.status_canonical
        data.start_date = null
        data.campaign_template_last_used = null

        data.draft = !(this.$route.name === 'CopyTemplateCampaign')
        data.is_campaign_template = this.$route.name === 'CopyTemplateCampaign'
      }

      if (this.$route.name === 'CopyCampaign') {
        data.start_date = this.nowDate
        data.start_date_time = this.nowTime
      }

      if (
        this.$route.query.template &&
        data.is_intelligence &&
        !$hasFeature('customer_intelligence_templates')
      ) {
        this.previewMode = true
      }

      if (
        (this.$route.name !== 'CopyCampaign' &&
          this.$route.name !== 'CreateFromTemplate' &&
          ['draft', 'scheduled', 'live'].includes(data.status_canonical)) ||
        data.is_campaign_template
      ) {
        const channelsWithErrors = []
        const disabledChannels = []

        Object.keys(channelLabels).forEach(c => {
          if (data.hasOwnProperty(c)) {
            if (data[c]) {
              const activeAppsExists = $hasPlatformActiveAppForChannel(c)
              const channelEnabled =
                $hasFeature(c) ||
                $hasFeature(_.camelCase(c)) ||
                $hasPlatformChannel(c)

              if (channelEnabled && !activeAppsExists) {
                channelsWithErrors.push(c)
              }
              if (!channelsWithErrors.includes(c) && !channelEnabled) {
                disabledChannels.push(c)
              }
            }
          }
        })

        if (disabledChannels.length + channelsWithErrors.length > 0) {
          let messages = []

          disabledChannels.forEach(c => {
            messages.push(
              this.$createElement(
                'div',
                { class: 'channel-problem-message' },
                this.$t('ChannelEnablement_ChannelNotAvailable', {
                  c: this.$t('ChannelEnablement_Channel_' + c)
                })
              )
            )
          })

          channelsWithErrors.forEach(c => {
            messages.push(
              this.$createElement(
                'div',
                { class: 'channel-problem-message' },
                this.$t('ChannelEnablement_ChannelWithError', {
                  c: this.$t('ChannelEnablement_Channel_' + c)
                })
              )
            )
          })

          customAlert(
            this.$t('ChannelEnablement_ProblemsWithChannels'),
            messages,
            null,
            false,
            ' channels-alert'
          )
        }
      }

      if (!data.ab_variants) {
        data.ab_variants = ['A']
      }

      if (
        this.$route.name === 'CopyCampaign' ||
        this.$route.name === 'Copy' ||
        this.$route.name === 'CopyTemplateCampaign'
      ) {
        data.title = data.title + ' Copy'

        // Delete winner
        data.variants = data.variants
          .filter(varinat => varinat.key !== 'W')
          .map(varinat => {
            varinat.winner = 0
            varinat.user_id = null
            varinat.winner_create_time = null
            return varinat
          })

        data.ab_variants = data.ab_variants.filter(v => v !== 'W')

        Object.keys(data.messages).forEach(ch => {
          delete data.messages[ch]['W']
        })

        data.automatically_pick_winner_schedule_status = null
        data.voucher_codes_file = null
      }

      if (
        this.$route.name === 'CopyCampaign' ||
        this.$route.name === 'Copy' ||
        this.$route.name === 'CopyTemplateCampaign' ||
        this.$route.name === 'CreateFromTemplate'
      ) {
        if ($hasFeature('broadcastDisabled')) {
          data.broadcast = 0
        }
      }

      if (
        this.$route.name === 'CopyCampaign' ||
        this.$route.name === 'Copy' ||
        this.$route.name === 'CopyTemplateCampaign' ||
        this.$route.name === 'CreateFromTemplate' ||
        data.status_canonical === 'draft'
      ) {
        if (!$hasFeature('allowVoucherCodes')) {
          data.voucher_codes_enabled = 0
        }
      }

      if (!data.languages_default) {
        data.languages_default = 'en'
      }

      if (!data.languages_list) {
        data.languages_list = ['en']
      }

      if (!data.languages_multi) {
        data.languages_multi = 0
      }

      if (!data.ab) {
        data.ab = 0
      }

      data.target_application_enabled = Number(data.target_application_enabled)

      if (data.messages.length === 0) {
        data.messages = {}
      }

      if (data.prepare_interval) {
        data.prepare_interval_enabled = 1
      }

      if (data.event_limit_per_user === null) {
        data.event_limit_per_user = undefined
      }

      if (
        data.encryption === null &&
        this.$store.state.app.project.push_encryption
      ) {
        data.encryption = 1
      }

      if (!data.conditions) {
        data.conditions = _.cloneDeep(this.dataConditionDefault)
      }
      if (!data.event_conditions) {
        data.event_conditions = _.cloneDeep(this.dataEventsConditionDefault)
      }
      if (!data.qualification_event_conditions) {
        data.qualification_event_conditions = _.cloneDeep(
          this.dataQualificationEventConditionDefault
        )
      }
      if (!data.accumulation_conditions) {
        data.accumulation_conditions = _.cloneDeep(
          this.dataAccumulationConditionDefault
        )
      }
      if (!data.early_cancel_event_conditions) {
        data.early_cancel_event_conditions = _.cloneDeep(
          this.dataEventsConditionDefault
        )
      }
      if (!data.goal_conditions) {
        data.goal_conditions = _.cloneDeep(this.dataGoalConditionDefault)
      }

      if (setModelData) {
        this.setModelData(data)
      }
      return data
    },
    setModelData(data) {
      this.model = { ...this.model, ..._.cloneDeep(data) }
      this.$nextTick(() => {
        this.$set(this.model, 'messages', _.cloneDeep(data.messages))
        this.$set(
          this.model,
          'goal_conditions',
          _.cloneDeep(data.goal_conditions)
        )
        this.$set(
          this.model,
          'event_conditions',
          _.cloneDeep(data.event_conditions)
        )
        this.$set(
          this.model,
          'qualification_event_conditions',
          _.cloneDeep(data.qualification_event_conditions)
        )
        this.$set(
          this.model,
          'accumulation_conditions',
          _.cloneDeep(data.accumulation_conditions)
        )
        this.$set(
          this.model,
          'early_cancel_event_conditions',
          _.cloneDeep(data.early_cancel_event_conditions)
        )
        this.initialStateModel = _.cloneDeep(this.model)
      })

      if (
        (this.$route.name === 'CopyCampaign' ||
          this.$route.name === 'CopyTemplateCampaign' ||
          this.$route.name === 'CreateFromTemplate') &&
        data.type === campaignTypeEnum.TYPE_WORKFLOW &&
        this.$route.query.id
      ) {
        this.copyWorkflow(this.$route.query.id).then(() => {
          if (this.workflowCopyIds.length > 0) {
            const variantKeys = Object.keys(
              data.messages[MessageTypeEnum.WORKFLOW]
            )
            variantKeys.forEach((key, index) => {
              if (this.workflowCopyIds[index]) {
                // prettier-ignore
                this.model.messages[MessageTypeEnum.WORKFLOW][key]['content']['workflow_id'] = this.workflowCopyIds[index]
              } else {
                // prettier-ignore
                this.model.messages[MessageTypeEnum.WORKFLOW][key]['content']['workflow_id'] = null
              }
            })
          }
        })

        this.model.is_open = 0
        this.model.status = 0
      }
    },
    getCampaignDiff(a, b) {
      return _.reduce(
        a,
        function(result, value, key) {
          return _.isEqual(value, b[key]) ? result : result.concat(key)
        },
        []
      )
    },
    calculateRecipients(samplingAllowed) {
      if (!this.recipients.polling) {
        this.$set(this.recipients, 'polling', true)
        this.$set(this.recipients, 'buttonsDisabled', true)
        this.$set(this.recipients, 'interruptPing', false)
        this.$set(
          this.recipients,
          'count',
          this.recipients.count || {
            channel: {},
            message_type: {}
          }
        )

        RestApi(
          projectEndpoint({
            method: 'post',
            url: `campaigns/calculate-recipients`,
            data: this.prepareRecipientData(samplingAllowed)
          })
        )
          .then(({ data }) => {
            this.$set(this.recipients, 'taskId', data.task_id)
            this.calculateRecipientsPoll(
              this.recipients.taskId,
              this.recipients.timer
            )
            this.$set(
              this.recipients,
              'descriptiveCountTime',
              Date.now() / 1000
            )
          })
          .catch(error => {
            this.$set(this.recipients, 'polling', false)
            this.$set(this.recipients, 'buttonsDisabled', false)
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      }
    },
    calculateRecipientsPoll(taskId, timer) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `task/records/${taskId}`
        })
      )
        .then(({ data }) => {
          switch (data.status) {
            case 0: // TASK_STATUS_NEW
            case 1: // TASK_STATUS_IN_PROGRESS
              if (timer === 0) {
                this.$set(this.recipients, 'buttonsDisabled', false)
              }
              if (!this.recipients.interruptPing) {
                this.$set(
                  this.recipients,
                  'statusTimeoutObject',
                  setTimeout(() => {
                    this.calculateRecipientsPoll(
                      taskId,
                      timer <= 0 ? 0 : --timer
                    )
                  }, 500)
                )
              } else {
                this.$set(this.recipients, 'polling', false)
                this.$set(this.recipients, 'buttonsDisabled', false)
                clearTimeout(this.recipients.statusTimeoutObject)
              }
              break
            case 2: // TASK_STATUS_DONE
              this.$set(this.recipients, 'count', data.result.count)
              this.$set(this.recipients, 'polling', false)
              this.$set(this.recipients, 'buttonsDisabled', false)
              this.$set(
                this.recipients,
                'descriptiveCountTime',
                Date.now() / 1000
              )
              break
            default:
              this.$set(this.recipients, 'polling', false)
              this.$set(this.recipients, 'buttonsDisabled', false)
              this.$message({
                message:
                  data.errors ||
                  this.$t('CampaignForm_CalculateRecipients_Error'),
                type: 'error'
              })
              break
          }
        })
        .catch(error => {
          this.$set(this.recipients, 'polling', false)
          this.$set(this.recipients, 'buttonsDisabled', false)
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    listRecipients() {
      if (!this.recipients.polling) {
        this.$set(this.recipients, 'polling', true)
        this.$set(this.recipients, 'buttonsDisabled', true)
        this.$set(this.recipients, 'interruptPing', false)

        RestApi(
          projectEndpoint({
            method: 'post',
            url: `campaigns/recipients-preview`,
            data: this.prepareRecipientData()
          })
        )
          .then(({ data }) => {
            this.$set(this.recipients, 'taskId', data.task_id)
            this.listRecipientsPoll(
              this.recipients.taskId,
              this.recipients.timer
            )
            this.$set(
              this.recipients,
              'descriptiveCountTime',
              Date.now() / 1000
            )
          })
          .catch(error => {
            this.$set(this.recipients, 'polling', false)
            this.$set(this.recipients, 'buttonsDisabled', false)
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      }
    },
    listRecipientsPoll(taskId, timer) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `task/records/${taskId}`
        })
      )
        .then(({ data }) => {
          switch (data.status) {
            case 0: // TASK_STATUS_NEW
            case 1: // TASK_STATUS_IN_PROGRESS
              if (timer === 0) {
                this.$set(this.recipients, 'buttonsDisabled', false)
              }
              if (!this.recipients.interruptPing) {
                this.$set(
                  this.recipients,
                  'statusTimeoutObject',
                  setTimeout(() => {
                    this.listRecipientsPoll(taskId, timer <= 0 ? 0 : --timer)
                  }, 500)
                )
              } else {
                this.$set(this.recipients, 'polling', false)
                this.$set(this.recipients, 'buttonsDisabled', false)
                clearTimeout(this.recipients.statusTimeoutObject)
              }
              break
            case 2: // TASK_STATUS_DONE
              this.$set(this.recipients, 'showRecipientsList', true)
              this.$set(this.recipients, 'polling', false)
              this.$set(this.recipients, 'buttonsDisabled', false)
              this.$set(
                this.recipients,
                'descriptiveCountTime',
                Date.now() / 1000
              )
              break
            default:
              this.$set(this.recipients, 'polling', false)
              this.$set(this.recipients, 'buttonsDisabled', false)
              this.$message({
                message:
                  data.errors ||
                  this.$t('CampaignForm_CalculateRecipients_Error'),
                type: 'error'
              })
              break
          }
        })
        .catch(error => {
          this.$set(this.recipients, 'polling', false)
          this.$set(this.recipients, 'buttonsDisabled', false)
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    checkRecipientCountStatus() {
      this.$set(this.recipients, 'interruptPing', false)
      RestApi(
        projectEndpoint({
          method: 'post',
          url: 'campaigns/recipients-status',
          data: this.prepareRecipientData()
        })
      ).then(response => {
        const { data } = response
        switch (data.status) {
          case null:
            this.$set(this.recipients, 'count', {
              channel: {},
              message_type: {}
            })
            this.$set(this.recipients, 'polling', false)
            this.$set(this.recipients, 'buttonsDisabled', false)
            break
          case 0:
          case 1:
            this.calculateRecipientsPoll(data.task_id, this.timer)
            break
          case 2:
            this.$set(this.recipients, 'count', data.result.count)
            this.$set(this.recipients, 'polling', false)
            this.$set(this.recipients, 'buttonsDisabled', false)
            break
          case 3:
          case 4:
            this.$set(this.recipients, 'polling', false)
            this.$set(this.recipients, 'buttonsDisabled', false)
            this.$message({
              message: data.result.error.message,
              type: 'error'
            })
            break
          default:
            this.$set(
              this.recipients,
              'count',
              this.recipients.count || {
                channel: {},
                message_type: {}
              }
            )
            this.$set(this.recipients, 'polling', false)
            this.$set(this.recipients, 'buttonsDisabled', false)
        }
        this.$set(
          this.recipients,
          'descriptiveCountTime',
          data.finish_time || data.start_time
        )
      })
    },
    interruptPing() {
      this.$set(this.recipients, 'interruptPing', true)
    },
    prepareRecipientData(samplingAllowed) {
      const model = _.cloneDeep(this.model)
      if (!model.target_application_enabled) {
        model.target_applications = []
      }

      model.target_applications = model.target_applications.join(',')
      model.samplingAllowed = samplingAllowed
      return model
    },
    checkAiEnablement() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: ``
        })
      ).then(({ data }) => {
        this.model.ai_enabled = data.ai_text_generation
        this.initialStateModel.ai_enabled = data.ai_text_generation
      })
    },
    checkEventType() {
      let systemEventEnabled =
        this.$hasFeature('subscriptionPreferences') &&
        this.$hasFeature('campaignTriggerByConsentPrefEvents')
      if (
        !systemEventEnabled &&
        !this.isPast &&
        this.model.event === this.campaignSystemEvents.EVENT_PREFERENCE_TRIGGER
      ) {
        this.model.event = 'custom'
        this.model.event_title = null
      }
    },
    handleSave() {
      if (this.$route.name === 'CreateCampaign') {
        this.model.draft = 1
      } else if (this.$route.name === 'CopyCampaign') {
        this.model.copied_from = this.$route.query.id
      }
      this.loading = true
      this.localModel = _.cloneDeep(this.model)

      this.formatModelDates()
      this.setVariantProps()
      this.setLanguageProps()
      this.setLocations()
      this.setTargetApplications()
      this.checkEventsData()

      RestApi(this.saveEndpoint)
        .then(({ data }) => {
          if (!this.model.id) {
            this.model.id = data.id
          }
          this.lastModifiedData.editStartTime = Math.floor(Date.now() / 1000)
          this.initialStateModel = _.cloneDeep(this.model)
          if (
            this.$route.name === 'CreateCampaign' ||
            this.$route.name === 'CopyCampaign' ||
            this.$route.name === 'CreateFromTemplate'
          ) {
            this.saveRecentlyViewedCampaign(data.id)

            this.$router.push(
              projectRoute({
                name: 'UpdateCampaign',
                hash: this.$route.hash,
                query: { ...this.$route.query, id: data.id }
              })
            )
          } else {
            this.reloadCampaignFromServer()
          }

          this.$message.success(this.$t('CampaignForm_Saved'))
        })
        .catch(error => {
          this.$message.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    reloadCampaignFromServer() {
      if (!this.model.id) {
        return
      }
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `campaigns/${this.model.id}`,
          params: {
            expand: this.fields,
            for: 'vue'
          }
        })
      )
        .then(({ data }) => {
          this.processData(data)
        })
        .catch(() => {
          // leave the model as-is; the save itself succeeded
        })
    },
    handleActions(action) {
      switch (action) {
        case 'save':
          this.checkLastEditedAndHandleSave()
          break
        case 'saveAsDraft':
          this.validate(1)
          break
        case 'saveAsTemplate':
          this.validate(0, 1)
          break
      }
    },
    handleVisibilityChange() {
      if (
        document.visibilityState === 'visible' &&
        ['UpdateCampaign', 'ViewCampaign'].includes(this.$route.name)
      ) {
        this.saveRecentlyViewedCampaign(this.$route.query.id)
      }
    },
    discardChangesConfirm() {
      return customConfirm(
        this.$t('CampaignForm_Confirm_Title'),
        this.$t('CampaignForm_Confirm_Message'),
        this.$t('CampaignForm_Confirm_Button_Leave')
      )
    },

    confirmLeave() {
      if (
        this.isAccessForbidden ||
        this.isCampaignSnapshotRoute ||
        this.$route.name === 'ViewTemplateCampaign' ||
        _.isEqual(this.model, this.initialStateModel)
      ) {
        return Promise.resolve()
      }

      return this.discardChangesConfirm().then(() => {
        this.model = _.cloneDeep(this.initialStateModel)
      })
    }
  },
  watch: {
    'model.event_filter': {
      handler() {
        this.model.event_conditions = this.dataEventsConditionDefault
      }
    },
    'model.qualification_event_filter': {
      handler() {
        this.model.qualification_event_conditions = this.dataQualificationEventConditionDefault
      }
    },
    'model.early_cancel_event_filter': {
      handler() {
        this.model.early_cancel_event_conditions = this.dataEventsConditionDefault
      }
    },
    'model.conversions': {
      handler() {
        this.model.goal_conditions = this.dataGoalConditionDefault
      }
    },
    'model.event': {
      handler() {
        this.checkEventType()
      }
    },
    $route(to) {
      this.updateFromRoute(to)
    },
    'model.ios_push': {
      handler(val) {
        this.updateMessage(channelTypes.ios_push, val)
      }
    },
    'model.android_push': {
      handler(val) {
        this.updateMessage(channelTypes.android_push, val)
      }
    },
    'model.web_push': {
      handler(val) {
        this.updateMessage(channelTypes.web_push, val)
      }
    },
    'model.whatsapp': {
      handler(val) {
        this.updateMessage(channelTypes.whatsapp, val)
      }
    },
    'model.facebook': {
      handler(val) {
        this.updateMessage(channelTypes.facebook, val)
      }
    },
    'model.email': {
      handler(val) {
        this.updateMessage(channelTypes.email, val)
      }
    },
    'model.inapp': {
      handler(val) {
        this.updateMessage(channelTypes.inapp, val)
      }
    },
    'model.onsite': {
      handler(val) {
        this.updateMessage(channelTypes.onsite, val)
      }
    },
    'model.sms': {
      handler(val) {
        this.updateMessage(channelTypes.sms, val)
      }
    },
    'model.webhook': {
      handler(val) {
        this.updateMessage(channelTypes.webhook, val)
      }
    },
    'model.type': {
      handler(val) {
        if (val === campaignTypeEnum.TYPE_WORKFLOW) {
          this.model.workflow = 1
          this.updateMessage(channelTypes.workflow, 1)
        }
      }
    },
    'model.inbox': {
      handler(val) {
        this.updateMessage(channelTypes.inbox, val)
      }
    },
    'model.ab': {
      handler() {
        if (!this.model.ab) {
          this.oldMessages = _.cloneDeep(this.messages)
        }
        Object.keys(this.messages).forEach(ch => {
          this.updateMessage(ch)
        })
        if (this.model.ab && this.model.promotion) {
          this.updatePromotionVariants()
        }
      }
    },
    'model.languages_multi': {
      handler() {
        if (!this.model.languages_multi) {
          this.oldMessages = _.cloneDeep(this.messages)
        }
        Object.keys(this.messages).forEach(ch => {
          this.updateMessage(ch)
        })
      }
    },
    'model.variants': {
      handler() {
        this.updateVariants()
      }
    },
    'model.languages_list': {
      handler() {
        this.updateLanguages()
      }
    },
    'model.persist_type': {
      handler(val) {
        if (val === 0) {
          this.$set(this.model, 'persist_for_period', null)
          this.$set(this.model, 'prepare_interval', null)
          this.$set(this.model, 'persist_for', null)
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.wrapper {
  width: 100%;
}

.btn-save-template {
  border-color: #1d87c9;
  color: #1d87c9;
}

.three-dot-menu {
  margin-right: 5px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  width: 20px;
}

.el-icon-more {
  transform: rotate(90deg);
}

.menu-items {
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  justify-content: start;
  flex-direction: column;

  button {
    color: #7e8596;
    font-weight: 400;
    font-size: 14px;
  }
}

.icon {
  margin-bottom: -2px;
  margin-right: 5px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/pages/CreateCampaign.vue